import { useState, useCallback, useRef } from "react";

export type StatusTimingConfig = {
  minStatusDurationMs: number;
  escalationDelayMs: number;
};

export const DEFAULT_STATUS_TIMING: StatusTimingConfig = {
  // Each status is held for at least this long to prevent flickering — tool calls
  // can complete in under a second, which would cause statuses to flash briefly.
  // A queue ensures every status is shown in order and never dropped, even when
  // events arrive before the minimum duration has elapsed.
  minStatusDurationMs: 2000,
  escalationDelayMs: 10000,
};

type StatusQueueItem = { message: string | null; onShown?: () => void };

export function useStatusQueue(statusTiming: StatusTimingConfig) {
  const statusTimingRef = useRef(statusTiming);
  statusTimingRef.current = statusTiming;

  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const statusQueueRef = useRef<StatusQueueItem[]>([]);
  const statusAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const statusShownAtRef = useRef<number>(0);
  const escalationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelEscalation = useCallback(() => {
    if (escalationTimerRef.current) {
      clearTimeout(escalationTimerRef.current);
      escalationTimerRef.current = null;
    }
  }, []);

  const clearStatusQueue = useCallback(() => {
    statusQueueRef.current = [];
    if (statusAdvanceTimerRef.current) {
      clearTimeout(statusAdvanceTimerRef.current);
      statusAdvanceTimerRef.current = null;
    }
    cancelEscalation();
    statusShownAtRef.current = 0;
    setStatusMessage(null);
  }, [cancelEscalation]);

  const advanceStatusQueue = useCallback(() => {
    statusAdvanceTimerRef.current = null;
    const item = statusQueueRef.current.shift();
    if (!item) return;
    cancelEscalation();
    setStatusMessage(item.message);
    statusShownAtRef.current = Date.now();
    item.onShown?.();
    if (statusQueueRef.current.length > 0) {
      statusAdvanceTimerRef.current = setTimeout(
        advanceStatusQueue,
        statusTimingRef.current.minStatusDurationMs,
      );
    }
  }, [cancelEscalation]);

  const enqueueStatus = useCallback(
    (message: string | null, onShown?: () => void) => {
      if (message === null) {
        cancelEscalation();
        statusQueueRef.current.forEach((item) => {
          item.onShown = undefined;
        });
        if (statusQueueRef.current.some((i) => i.message === null)) return;
      }
      statusQueueRef.current.push({ message, onShown });
      if (statusAdvanceTimerRef.current) return;
      const elapsed = Date.now() - statusShownAtRef.current;
      const delay = Math.max(
        0,
        statusTimingRef.current.minStatusDurationMs - elapsed,
      );
      statusAdvanceTimerRef.current = setTimeout(advanceStatusQueue, delay);
    },
    [advanceStatusQueue, cancelEscalation],
  );

  const scheduleEscalation = useCallback(
    (message: string) => {
      cancelEscalation();
      escalationTimerRef.current = setTimeout(() => {
        escalationTimerRef.current = null;
        enqueueStatus(message);
      }, statusTimingRef.current.escalationDelayMs);
    },
    [enqueueStatus, cancelEscalation],
  );

  return {
    statusMessage,
    enqueueStatus,
    clearStatusQueue,
    cancelEscalation,
    scheduleEscalation,
  };
}
